import { Controller } from "@hotwired/stimulus"

// A toolbar filter/sort menu. The trigger toggles a small radio-option menu; choosing an
// option writes the value into this dropdown's hidden field and submits the toolbar form
// (a Turbo-Frame GET), so the list re-renders with no page reload. Closes on outside-click
// or Escape.
export default class extends Controller {
  static targets = ["menu", "field", "label"]
  // Sidebar rows live in a scrolling overflow:auto pane. An absolute menu is
  // clipped there (and can land below the viewport), so those dropdowns pin to
  // the trigger with position:fixed instead.
  static values = { fixed: Boolean }

  connect() {
    this.closeOnOutside = this.closeOnOutside.bind(this)
    this.closeOnEscape = this.closeOnEscape.bind(this)
  }

  disconnect() {
    this.removeListeners()
  }

  toggle() {
    this.menuTarget.hidden ? this.open() : this.close()
  }

  open() {
    this.menuTarget.hidden = false
    this.button.setAttribute("aria-expanded", "true")
    if (this.fixedValue) {
      this.button.scrollIntoView({ block: "nearest", inline: "nearest" })
      this.pinToButton()
    }
    document.addEventListener("click", this.closeOnOutside)
    document.addEventListener("keydown", this.closeOnEscape)
  }

  close() {
    this.menuTarget.hidden = true
    this.button.setAttribute("aria-expanded", "false")
    this.unpin()
    this.removeListeners()
    this.dispatch("closed")
  }

  reposition() {
    if (this.fixedValue && !this.menuTarget.hidden) this.pinToButton()
  }

  pinToButton() {
    const rect = this.button.getBoundingClientRect()
    const menu = this.menuTarget
    const gap = 4
    menu.style.position = "fixed"
    menu.style.left = "auto"
    menu.style.right = `${Math.max(8, window.innerWidth - rect.right)}px`
    const height = menu.offsetHeight || 80
    const below = rect.bottom + gap
    const opensUp = below + height > window.innerHeight && rect.top > height + gap
    menu.style.top = `${opensUp ? rect.top - height - gap : below}px`
  }

  unpin() {
    const menu = this.menuTarget
    menu.style.position = ""
    menu.style.left = ""
    menu.style.right = ""
    menu.style.top = ""
  }

  select(event) {
    // The toolbar isn't re-rendered on submit (only the inner list frame is), so move the
    // checked state to the chosen option here.
    const chosen = event.currentTarget
    this.menuTarget.querySelectorAll("[role='menuitemradio']").forEach((option) => {
      option.setAttribute("aria-checked", option === chosen ? "true" : "false")
    })
    this.fieldTarget.value = event.params.value
    if (this.hasLabelTarget) this.labelTarget.textContent = chosen.textContent.trim()
    this.close()
    this.element.closest("form").requestSubmit()
  }

  closeOnOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  closeOnEscape(event) {
    if (event.key === "Escape") {
      this.close()
      this.button.focus()
    }
  }

  removeListeners() {
    document.removeEventListener("click", this.closeOnOutside)
    document.removeEventListener("keydown", this.closeOnEscape)
  }

  get button() {
    return this.element.querySelector("[aria-haspopup]")
  }
}
